// Added by Sejune Oh on 2019-03-22
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { useEffect, useState, forwardRef, RefObject, useImperativeHandle } from 'react'
import { MuiThemeProvider } from '@material-ui/core'
import _ from 'lodash'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Define
import {
  ReactInputChangeEvent,
  ReactBlurEvent,
  InputValidator,
  InputBlurValidator,
  ReactKeyUpEvent,
} from '@/helper/define'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Component
import Input, { InputBaseFCProps } from './Input'
import NormalInput from './NormalInput'
import PasswordInput from './PasswordInput'

// Custom Theme
import CoreLineTheme, { DEFAULT_INPUT_VARIANT } from '@/styles/Theme'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
interface InputCProps extends InputBaseFCProps {
  isPassword?: boolean
  validator?: InputValidator
  blurValidator?: InputBlurValidator
  onInputKeyUp?: ReactKeyUpEvent
  forwardref?: RefObject<any>
  description?: string
  allowWhiteSpace?: boolean
  onChange?: Function
  onBlur?: Function
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const InputContainer = forwardRef(
  (
    {
      allowWhiteSpace,
      defaultValue,
      value: propValue,
      isPassword,
      isFullWidth,
      condition,
      variant = DEFAULT_INPUT_VARIANT,
      description,
      forwardref: propsForwardRef,
      hidden,
      id,
      onBlur,
      blurValidator,
      onChange,
      validator,
      ...props
    }: InputCProps,
    ref
  ) => {
    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // state
    const [isErrorOccured, setIsErrorOccured] = useState<boolean>(false)
    const [errMessage, setErrMessage] = useState<string>('')
    const [value, setValue] = useState<any>('')

    useImperativeHandle(
      ref,
      () => ({
        value,
        isErrorOccured,
        errMessage,
        setValue: handleSetValue,
        setErrorState,
        clear: () => {
          setValue('')
          const $input = document.getElementById(id!) as HTMLInputElement
          if ($input) $input.value = ''
        },
        callValidator: () => {
          if (!validator) return

          const [validateStatus, errMessage] = validator!(value)
          setErrorState({ validateStatus, errMessage })
        },
        callBlurValidator: async () => {
          if (!blurValidator) return

          const [validateStatus, errMessage] = await blurValidator!(value)
          setErrorState({ validateStatus, errMessage })
        },
      }),
      [ref, value, isErrorOccured, errMessage]
    )

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // handlers
    const handleSetValue = (value: any) => {
      setValue(value)
      const $input = document.getElementById(id!) as HTMLInputElement
      if ($input) $input.value = value.toString()
    }

    const handleChange: ReactInputChangeEvent = (event) => {

      handleSetValue(event.currentTarget.value)

      let validateStatus: boolean = false
      let errMessage: string | undefined

      if (onChange) onChange(event)

      if (!validator) return
      ;[validateStatus, errMessage] = validator!(event.currentTarget.value)
      
      setErrorState({ validateStatus, errMessage })
    }

    const handleBlur: ReactBlurEvent = async (event) => {
      const currentTarget = event.currentTarget as HTMLInputElement
      let curValue = currentTarget.value

      if (!allowWhiteSpace) {
        curValue = currentTarget.value.trim()
        handleSetValue(curValue)
      }
      if (onBlur) return onBlur(event)

      if (isErrorOccured) return

      if (!blurValidator) return
      const [validateStatus, errMessage] = await blurValidator(curValue)
      setErrorState({ validateStatus, errMessage })
    }

    const setErrorState = ({ validateStatus, errMessage = '' }: { validateStatus: boolean; errMessage?: string }) => {
      setIsErrorOccured(validateStatus)
      setErrMessage(errMessage)
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // effect
    useEffect(() => {
      let defValue = defaultValue
      if (_.isNil(defValue)) defValue = propValue
      if (!_.isNil(defValue)) handleSetValue(defValue)
    }, [])

    const baseProps = {
      id,
      onChange: handleChange,
      onBlur: handleBlur,
      isErrorOccured,
      variant,
      ref: propsForwardRef,
    }

    return (
      <MuiThemeProvider theme={CoreLineTheme}>
        <Input
          id={id}
          value={value}
          errMessage={errMessage}
          isErrorOccured={isErrorOccured}
          isFullWidth={isFullWidth}
          hidden={hidden}
          condition={condition}
          description={description}
          inputComponent={
            isPassword ? (
              <PasswordInput {...props} {...baseProps} />
            ) : (
              <NormalInput {...props} {...baseProps} curValue={value} />
            )
          }
          {...props}
        />
      </MuiThemeProvider>
    )
  }
)

export default InputContainer
